// ==========================================
// FILE: src/components/PersonalMessages.js
// ==========================================

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye, FiFile, FiVideo, FiMic, FiImage, FiType } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

import PageScaffold from "./ui/PageScaffold";
import Modal from "./ui/Modal";
import VideoLightbox from "./ui/VideoLightbox";
import {
  useUserContacts,
  groupContactsForOptions,
  useAddContactAutoSelect,
  AddContactModal,
} from "../utils/contacts-ui";
import { useAccess } from "../access/AccessContext";
import {
  toStoragePath,
  openDownloadForAttachment,
  downloadTextFile,
  buildForwardMailto,
} from "../utils/managerActions";
import "../styles/layout.css";

export default function PersonalMessages({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const inferAttachmentKindFromUrl = (url) => {
    const clean = (url || "").split("?")[0].toLowerCase();
    if (!clean) return "text";

    if (/(\.mp3|\.wav|\.m4a|\.aac|\.oga|\.ogg)$/i.test(clean)) return "audio";
    if (/\.webm$/i.test(clean) && /(audio|voice|mic)/i.test(clean)) return "audio";

    if (/(\.mp4|\.m4v|\.mov|\.webm|\.ogv)$/i.test(clean)) return "video";
    if (/(\.png|\.jpe?g|\.gif|\.webp|\.bmp|\.svg)$/i.test(clean)) return "picture";
    return "file";
  };

  const acceptForKind = (kind) => {
    if (kind === "video") return "video/*";
    if (kind === "audio") return "audio/*";
    if (kind === "picture") return "image/*";
    return undefined;
  };

  const isRecordingSupported = (kind) => {
    const hasGUM = typeof window !== "undefined" && !!(navigator?.mediaDevices?.getUserMedia);
    if (!hasGUM) return false;
    if (kind === "picture") return true;
    return typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined";
  };

  const kindLabel = (k) => {
    if (k === "picture") return "Image";
    if (k === "video") return "Video";
    if (k === "audio") return "Audio";
    if (k === "file") return "File";
    return "Text";
  };

  // Resolve storage path from a message record (best-effort)
  const resolveStoragePathFromMessage = (msg) => {
    if (!msg) return null;
    if (msg.filePath) return msg.filePath;

    // If Firebase Storage URL: decode "/o/<encodedPath>?"
    const derivedFromUrl = (() => {
      try {
        const enc = msg?.fileUrl?.split("/o/")[1]?.split("?")[0] || "";
        return enc ? decodeURIComponent(enc) : null;
      } catch {
        return null;
      }
    })();
    if (derivedFromUrl) return derivedFromUrl;

    // Last fallback
    if (msg.fileName && user?.uid) return `personalMessages/${user.uid}/${msg.fileName}`;
    return null;
  };

  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showMsgModal, setShowMsgModal] = useState(false);
  const [activeMsg, setActiveMsg] = useState(null);

  const { contacts } = useUserContacts(user);
  const grouped = useMemo(() => groupContactsForOptions(contacts), [contacts]);

  const onContactCreated = (c) => {
    setForContactId(c.id);
    setForContactName(c.name);
  };
  const { open: showContactModal, setOpen: setShowContactModal, openModal: openAddContact } =
    useAddContactAutoSelect(contacts, onContactCreated);

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [composeKind, setComposeKind] = useState("text");
  const [forContactId, setForContactId] = useState("");
  const [forContactName, setForContactName] = useState("");
  const messageRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  // NEW: flag to remove existing attachment on save
  const [removeExistingAttachment, setRemoveExistingAttachment] = useState(false);

  // Action menu (Upload vs Record/Take)
  const [actionMenuKind, setActionMenuKind] = useState(null);
  const [pickerAccept, setPickerAccept] = useState(undefined);

  // Recorder/camera state
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const liveVideoRef = useRef(null);
  const [recState, setRecState] = useState({
    open: false,
    kind: null,
    status: "idle",
    isRecording: false,
    error: "",
  });

  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // Read-only view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewMsg, setViewMsg] = useState(null);

  const stopStream = () => {
    const s = streamRef.current;
    if (s) {
      try {
        s.getTracks().forEach((t) => t.stop());
      } catch {
        // ignore
      }
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch {
        // ignore
      }
      stopStream();
    };
  }, []);

  useEffect(() => {
    if (!user || !user.uid) return;
    const q = query(
      collection(db, "personalMessages"),
      where("uid", "==", user.uid),
      orderBy("created", "desc")
    );
    return onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError((err && err.message) || "Listen error")
    );
  }, [user && user.uid]);

  function openMsgModalFn(msg) {
    if (!msg && free && messages.length >= 1) {
      setShowUpgrade(true);
      return;
    }
    if (readOnly) {
      setViewMsg(msg || null);
      setViewOpen(true);
      return;
    }

    setActionMenuKind(null);
    setPickerAccept(undefined);
    setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
    stopStream();

    const m = msg || null;
    setActiveMsg(m);
    setText((m && m.text) || "");
    setForContactId((m && m.forContactId) || "");
    setForContactName((m && m.forContactName) || "");
    setFile(null);
    setPreviewUrl((m && m.fileUrl) || "");
    setComposeKind(
      (m && m.attachmentKind) || (m && m.fileUrl ? inferAttachmentKindFromUrl(m.fileUrl) : "text")
    );
    setRemoveExistingAttachment(false); // reset
    setError("");
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
    setShowMsgModal(true);
  }

  function closeMsgModal() {
    setShowMsgModal(false);
    setActiveMsg(null);
    setText("");
    setFile(null);
    setPreviewUrl("");
    setComposeKind("text");
    setForContactId("");
    setForContactName("");
    setError("");
    setUploading(false);
    setActionMenuKind(null);
    setPickerAccept(undefined);
    setRemoveExistingAttachment(false);
    setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
    stopStream();
  }

  function handleFileChange(e) {
    const f = (e.target && e.target.files && e.target.files[0]) || null;
    setRemoveExistingAttachment(false);
    setFile(f);
    setPreviewUrl(f ? URL.createObjectURL(f) : "");
    setActionMenuKind(null);
  }

  function handleForChange(e) {
    const val = e.target.value;
    if (val === "__ADD__") {
      openAddContact();
      return;
    }
    setForContactId(val);
    const sel = contacts.find((c) => c.id === val);
    setForContactName(sel ? sel.name : "");
  }

  function resetAttachmentSelection(nextKind) {
    setError("");
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    setPreviewUrl(activeMsg && activeMsg.fileUrl && nextKind !== "text" ? activeMsg.fileUrl : "");
  }

  function openUploadPicker(kind) {
    if (readOnly) return;

    setRemoveExistingAttachment(false);

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // ignore
    }
    stopStream();
    setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });

    setComposeKind(kind);
    setActionMenuKind(null);
    setPickerAccept(acceptForKind(kind));
    resetAttachmentSelection(kind);

    requestAnimationFrame(() => {
      if (fileInput.current) fileInput.current.click();
    });
  }

  const pickMimeType = (kind) => {
    try {
      if (typeof window === "undefined" || !window.MediaRecorder || !window.MediaRecorder.isTypeSupported)
        return "";
      const candidates =
        kind === "audio"
          ? ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]
          : [
              "video/webm;codecs=vp9,opus",
              "video/webm;codecs=vp8,opus",
              "video/webm",
              "video/mp4",
            ];
      return candidates.find((t) => window.MediaRecorder.isTypeSupported(t)) || "";
    } catch {
      return "";
    }
  };

  const extForMime = (mime, kind) => {
    const m = String(mime || "").toLowerCase();
    if (kind === "picture") return "jpg";
    if (m.includes("mp4")) return kind === "audio" ? "m4a" : "mp4";
    if (m.includes("webm")) return "webm";
    return kind === "audio" ? "webm" : "webm";
  };

  async function openRecorder(kind) {
    if (readOnly) return;

    setRemoveExistingAttachment(false);
    setError("");
    setActionMenuKind(null);

    if (!isRecordingSupported(kind)) {
      setError("Recording isn’t supported on this browser/device. Please upload instead.");
      openUploadPicker(kind);
      return;
    }

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // ignore
    }
    stopStream();

    setComposeKind(kind);
    resetAttachmentSelection(kind);
    setRecState({ open: true, kind, status: "requesting", isRecording: false, error: "" });

    try {
      const constraints =
        kind === "audio"
          ? { audio: true, video: false }
          : kind === "video"
          ? { audio: true, video: { facingMode: "environment" } }
          : { audio: false, video: { facingMode: "environment" } };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (kind !== "audio") {
        requestAnimationFrame(() => {
          try {
            if (liveVideoRef.current) {
              liveVideoRef.current.srcObject = stream;
              liveVideoRef.current.play?.().catch(() => {});
            }
          } catch {
            // ignore
          }
        });
      }

      setRecState({ open: true, kind, status: "ready", isRecording: false, error: "" });
    } catch (err) {
      stopStream();
      setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
      setError((err && err.message) || "Could not access your microphone/camera.");
    }
  }

  function startRecording() {
    if (!recState.open) return;
    if (recState.kind === "picture") return;

    setRemoveExistingAttachment(false);

    const kind = recState.kind;
    const stream = streamRef.current;
    if (!stream) return;

    chunksRef.current = [];

    const mimeType = pickMimeType(kind);
    let mr;
    try {
      mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      mr = new MediaRecorder(stream);
    }

    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    mr.onstop = () => {
      try {
        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || (kind === "audio" ? "audio/webm" : "video/webm"),
        });
        const ext = extForMime(blob.type, kind);
        const fileName = `${kind}_recording_${Date.now()}.${ext}`;
        const recordedFile = new File([blob], fileName, { type: blob.type });

        setFile(recordedFile);
        setPreviewUrl(URL.createObjectURL(blob));
      } finally {
        setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
        stopStream();
      }
    };

    mr.start();
    setRecState((s) => ({ ...s, status: "recording", isRecording: true }));
  }

  function stopRecording() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        setRecState((s) => ({ ...s, status: "processing", isRecording: false }));
        mediaRecorderRef.current.stop();
      }
    } catch {
      setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
      stopStream();
    }
  }

  async function capturePhoto() {
    try {
      setRemoveExistingAttachment(false);

      const v = liveVideoRef.current;
      if (!v) return;

      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 720;

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(v, 0, 0, w, h);

      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      if (!blob) throw new Error("Capture failed");

      const captured = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      setFile(captured);
      setPreviewUrl(URL.createObjectURL(blob));

      setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
      stopStream();
    } catch (err) {
      setError((err && err.message) || "Could not take the photo.");
    }
  }

  function closeRecorder() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // ignore
    }
    setRecState({ open: false, kind: null, status: "idle", isRecording: false, error: "" });
    stopStream();
  }

  function chooseKind(kind) {
    if (readOnly) return;

    setError("");
    setActionMenuKind(null);

    if (recState.open) closeRecorder();

    if (kind === "text") {
      setComposeKind("text");
      resetAttachmentSelection("text");
      requestAnimationFrame(() => {
        if (messageRef.current) messageRef.current.focus({ preventScroll: true });
      });
      return;
    }

    if (kind === "file") {
      openUploadPicker("file");
      return;
    }

    setComposeKind(kind);
    resetAttachmentSelection(kind);
    setActionMenuKind(kind);
  }

  // NEW: remove attachment (does not commit until Save)
  function markRemoveAttachment() {
    if (readOnly) return;
    setRemoveExistingAttachment(true);
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    setPreviewUrl("");
    setComposeKind("text");
  }

  function clearNewSelection() {
    if (readOnly) return;
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    // If editing and not marked for removal, revert preview to existing attachment
    if (activeMsg?.fileUrl && !removeExistingAttachment) {
      setPreviewUrl(activeMsg.fileUrl);
    } else {
      setPreviewUrl("");
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (readOnly) return;

    const hasExistingAttachment = !!(activeMsg && activeMsg.fileUrl) && !removeExistingAttachment;
    const hasAnyAttachment = !!file || hasExistingAttachment;
    if (!text.trim() && !hasAnyAttachment) {
      setError("Add some text or attach a file.");
      return;
    }

    setUploading(true);
    try {
      let fileUrl = (activeMsg && activeMsg.fileUrl) || null;
      let fileName = (activeMsg && activeMsg.fileName) || null;
      let filePath = (activeMsg && activeMsg.filePath) || null;

      const existingKind =
        (activeMsg && activeMsg.attachmentKind) ||
        (activeMsg && activeMsg.fileUrl ? inferAttachmentKindFromUrl(activeMsg.fileUrl) : "text");

      // If we are editing and either removing or replacing, delete the old attachment first (best-effort)
      const shouldDeleteOld =
        !!activeMsg && !!activeMsg.fileUrl && (removeExistingAttachment || !!file);

      if (shouldDeleteOld) {
        const oldPath = resolveStoragePathFromMessage(activeMsg);
        if (oldPath) {
          try {
            await deleteObject(storageRef(storage, oldPath));
          } catch {
            // ignore missing/deletion errors
          }
        }
      }

      // Apply removal
      if (removeExistingAttachment && !file) {
        fileUrl = null;
        fileName = null;
        filePath = null;
      }

      // Upload new file if provided
      let attachmentKind = fileUrl ? existingKind : "text";

      if (file) {
        const safeBase = `${composeKind}_${Date.now()}_${String(file.name || "upload")}`.replace(/\s+/g, "_");
        fileName = `${user.uid}_${safeBase}`;
        filePath = `personalMessages/${user.uid}/${fileName}`;
        const sref = storageRef(storage, filePath);
        const task = uploadBytesResumable(sref, file);
        await new Promise((res, rej) => task.on("state_changed", null, rej, res));
        fileUrl = await getDownloadURL(task.snapshot.ref);

        attachmentKind = composeKind;
      } else {
        // if we removed it, it becomes text
        if (!fileUrl) attachmentKind = "text";
      }

      const payload = {
        text,
        fileUrl,
        fileName,
        filePath,
        attachmentKind: fileUrl ? attachmentKind : "text",
        forContactId: forContactId || null,
        forContactName: forContactName || null,
        updated: serverTimestamp(),
      };

      if (activeMsg) {
        await updateDoc(doc(db, "personalMessages", activeMsg.id), payload);
      } else {
        await addDoc(collection(db, "personalMessages"), {
          uid: user.uid,
          created: serverTimestamp(),
          ...payload,
        });
      }

      closeMsgModal();
    } catch (err) {
      setError((err && err.message) || "Failed to save");
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    if (showMsgModal && messageRef.current) {
      messageRef.current.focus({ preventScroll: true });
    }
  }, [showMsgModal]);

  async function handleDelete(msg) {
    if (readOnly) return;
    if (!window.confirm("Delete this message?")) return;
    try {
      const path = resolveStoragePathFromMessage(msg);
      if (path) {
        try {
          await deleteObject(storageRef(storage, path));
        } catch {
          // ignore
        }
      }
      await deleteDoc(doc(db, "personalMessages", msg.id));
    } catch (err) {
      alert((err && err.message) || "Failed to delete");
    }
  }

  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  function openView(msg) {
    setViewMsg(msg);
    setViewOpen(true);
  }
  function closeView() {
    setViewOpen(false);
    setViewMsg(null);
  }

  return (
    <>
      <PageScaffold
        title="Personal Messages"
        onAdd={!readOnly ? () => openMsgModalFn(null) : undefined}
        heroSrc="/lastwish-account-Personal-Mesages.jpg"
        heroAlt="Personal Messages"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
      >
        <div className="dv-list">
          {messages.length === 0 ? (
            <div className="empty-state">No messages yet.</div>
          ) : (
            messages.map((msg) => {
              const kind = msg.fileUrl
                ? msg.attachmentKind || inferAttachmentKindFromUrl(msg.fileUrl)
                : "text";
              const isImg = kind === "picture";
              const isVid = kind === "video";
              const isAud = kind === "audio";
              const firstLine = (msg.text || "").split(/\r?\n/)[0].trim();
              const title = firstLine || "Personal Message";
              const subWho = msg.forContactName
                ? `For: ${msg.forContactName}`
                : msg.forContactId
                ? `For: ${msg.forContactId}`
                : "";
              const subAtt = msg.fileUrl ? `Attachment: ${kindLabel(kind)}` : "";
              const PrimaryBtn = () => (
                <button
                  className="btn btn-main btn-edit"
                  onClick={() => (readOnly ? openView(msg) : openMsgModalFn(msg))}
                  {...(readOnly ? { "data-allow-readonly": "1" } : {})}
                >
                  {readOnly ? "View" : "Edit"}
                </button>
              );
              return (
                <div key={msg.id} className="card dv-item">
                  <div className="dv-left">
                    {msg.fileUrl ? (
                      isImg ? (
                        <img src={msg.fileUrl} alt="" className="tile-thumb" />
                      ) : isVid ? (
                        <video
                          src={msg.fileUrl}
                          className="tile-thumb"
                          preload="metadata"
                          muted
                          playsInline
                        />
                      ) : isAud ? (
                        <div
                          className="tile-thumb"
                          style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}
                        >
                          🎧
                        </div>
                      ) : (
                        <div
                          className="tile-thumb"
                          style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}
                        >
                          📄
                        </div>
                      )
                    ) : (
                      <div
                        className="tile-thumb"
                        style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}
                      >
                        📝
                      </div>
                    )}
                  </div>
                  <div className="dv-main">
                    <div className="tile-title">{title}</div>
                    {subWho && <div className="tile-sub">{subWho}</div>}
                    {subAtt && <div className="tile-sub">{subAtt}</div>}
                  </div>
                  <div className="dv-actions">
                    <PrimaryBtn />
                    {!readOnly && (
                      <button className="btn btn-danger" onClick={() => handleDelete(msg)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PageScaffold>

      {/* Editor Modal (owner only) */}
      <Modal
        open={!readOnly && showMsgModal}
        onClose={closeMsgModal}
        title={activeMsg ? "Edit Message" : "New Message"}
      >
        <form onSubmit={handleSave}>
          <div className="grid2">
            <div>
              <label className="label">Assign to</label>
              <select className="input" value={forContactId} onChange={handleForChange}>
                <option value="">Unassigned</option>
                <option value="__ADD__">+ Add new contact…</option>
                {grouped.map((g) =>
                  g.items.length ? (
                    <optgroup key={g.key} label={g.label}>
                      {g.items.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null
                )}
              </select>
            </div>

            <div>
              <label className="label">Add to message</label>
              <div className="pm-kind-grid">
                <button
                  type="button"
                  className={`pm-kind ${composeKind === "file" ? "active" : ""}`}
                  onClick={() => chooseKind("file")}
                >
                  <FiFile className="pm-kind-ico" />
                  <span>File</span>
                </button>
                <button
                  type="button"
                  className={`pm-kind ${composeKind === "video" ? "active" : ""}`}
                  onClick={() => chooseKind("video")}
                >
                  <FiVideo className="pm-kind-ico" />
                  <span>Video</span>
                </button>
                <button
                  type="button"
                  className={`pm-kind ${composeKind === "audio" ? "active" : ""}`}
                  onClick={() => chooseKind("audio")}
                >
                  <FiMic className="pm-kind-ico" />
                  <span>Audio</span>
                </button>
                <button
                  type="button"
                  className={`pm-kind ${composeKind === "picture" ? "active" : ""}`}
                  onClick={() => chooseKind("picture")}
                >
                  <FiImage className="pm-kind-ico" />
                  <span>Picture</span>
                </button>
                <button
                  type="button"
                  className={`pm-kind ${composeKind === "text" ? "active" : ""}`}
                  onClick={() => chooseKind("text")}
                >
                  <FiType className="pm-kind-ico" />
                  <span>Text</span>
                </button>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInput}
                type="file"
                onChange={handleFileChange}
                accept={pickerAccept}
                style={{ display: "none" }}
              />

              {/* Clean attachment display + remove/clear */}
              {(() => {
                const existingKind =
                  activeMsg?.fileUrl
                    ? (activeMsg.attachmentKind || inferAttachmentKindFromUrl(activeMsg.fileUrl))
                    : "text";

                const showExisting = !!activeMsg?.fileUrl && !removeExistingAttachment && !file;
                const showSelected = !!file;

                if (!showExisting && !showSelected && !removeExistingAttachment) return null;

                return (
                  <div className="pm-attach-bar">
                    {showSelected ? (
                      <>
                        <span className="pm-attach-pill">
                          Attachment: <b>{kindLabel(composeKind)}</b>
                        </span>
                        <button type="button" className="btn btn-cancel pm-attach-btn" onClick={clearNewSelection}>
                          Clear
                        </button>
                      </>
                    ) : removeExistingAttachment ? (
                      <>
                        <span className="pm-attach-pill warn">
                          Attachment will be removed when you save.
                        </span>
                        <button
                          type="button"
                          className="btn pm-attach-btn"
                          onClick={() => {
                            // undo removal and restore preview
                            setRemoveExistingAttachment(false);
                            if (activeMsg?.fileUrl) {
                              setPreviewUrl(activeMsg.fileUrl);
                              setComposeKind(existingKind);
                            }
                          }}
                        >
                          Undo
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="pm-attach-pill">
                          Attachment: <b>{kindLabel(existingKind)}</b>
                        </span>
                        <button type="button" className="btn btn-danger pm-attach-btn" onClick={markRemoveAttachment}>
                          Remove attachment
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Sub-menu for audio/video/picture */}
              {actionMenuKind && (
                <div className="pm-action-sheet">
                  <div className="pm-action-title">
                    {actionMenuKind === "picture"
                      ? "Picture"
                      : actionMenuKind === "video"
                      ? "Video"
                      : "Audio"}{" "}
                    options
                  </div>
                  <div className="pm-action-row">
                    <button
                      type="button"
                      className="btn btn-main"
                      onClick={() => openUploadPicker(actionMenuKind)}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => openRecorder(actionMenuKind)}
                    >
                      {actionMenuKind === "picture"
                        ? "Take a photo"
                        : actionMenuKind === "video"
                        ? "Record video"
                        : "Record audio"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-cancel"
                      onClick={() => setActionMenuKind(null)}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="pm-action-note">
                    {actionMenuKind === "audio"
                      ? "Record uses microphone only."
                      : actionMenuKind === "picture"
                      ? "Choose Upload or Take a photo."
                      : "Choose Upload or Record."}
                  </div>
                </div>
              )}

              {/* Recorder / camera panel */}
              {recState.open && (
                <div className="pm-rec-wrap">
                  <div className="pm-rec-head">
                    <div className="pm-rec-title">
                      {recState.kind === "picture"
                        ? "Take a photo"
                        : recState.kind === "video"
                        ? "Record a video"
                        : "Record audio"}
                    </div>
                    <button type="button" className="btn btn-cancel" onClick={closeRecorder}>
                      Close
                    </button>
                  </div>

                  {recState.kind !== "audio" && (
                    <video
                      ref={liveVideoRef}
                      className="pm-rec-video"
                      playsInline
                      muted
                      autoPlay
                    />
                  )}

                  <div className="pm-rec-actions">
                    {recState.kind === "picture" ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-main"
                          onClick={capturePhoto}
                          disabled={recState.status !== "ready"}
                        >
                          Take photo
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openUploadPicker("picture")}
                        >
                          Upload instead
                        </button>
                      </>
                    ) : (
                      <>
                        {!recState.isRecording ? (
                          <button
                            type="button"
                            className="btn btn-main"
                            onClick={startRecording}
                            disabled={recState.status !== "ready"}
                          >
                            Start
                          </button>
                        ) : (
                          <button type="button" className="btn btn-danger" onClick={stopRecording}>
                            Stop
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openUploadPicker(recState.kind)}
                        >
                          Upload instead
                        </button>
                      </>
                    )}
                  </div>

                  {recState.status === "requesting" && (
                    <div className="pm-rec-note">Requesting permission…</div>
                  )}
                  {recState.status === "recording" && (
                    <div className="pm-rec-note">Recording…</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <label className="label" style={{ marginTop: 8 }}>
            Message
          </label>
          <textarea
            key={activeMsg ? activeMsg.id : "new-message"}
            ref={messageRef}
            className="input"
            rows={5}
            defaultValue={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your message…"
          />

          {/* Preview (if any) */}
          {(() => {
            if (!previewUrl) return null;

            const kind = file
              ? composeKind
              : (activeMsg && activeMsg.attachmentKind) || inferAttachmentKindFromUrl(previewUrl);

            if (kind === "video") {
              return (
                <video
                  src={previewUrl}
                  className="pm-preview-video"
                  controls
                  playsInline
                  style={{ marginTop: 8 }}
                />
              );
            }
            if (kind === "audio") {
              return (
                <audio
                  src={previewUrl}
                  className="pm-preview-audio"
                  controls
                  style={{ marginTop: 8, width: "100%" }}
                />
              );
            }
            if (kind === "picture") {
              return <img src={previewUrl} alt="Preview" className="pm-preview-thumb" />;
            }
            return null;
          })()}

          {error && <div style={{ color: "#a00", marginTop: 8 }}>{error}</div>}

          <div className="form-actions">
            <button className="btn btn-main" disabled={uploading}>
              {activeMsg ? "Update" : "Save"}
            </button>
            {activeMsg && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleDelete(activeMsg)}
                disabled={uploading}
              >
                Delete
              </button>
            )}
            <button type="button" className="btn btn-cancel" onClick={closeMsgModal}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Read-only VIEW modal (managers) */}
      {readOnly && viewOpen && viewMsg && (
        <div className="modal-bg" onClick={closeView}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <h3>Personal Message</h3>
            <div className="modal-body">
              <div className="grid2" style={{ marginBottom: 10 }}>
                <div>
                  <label className="label">Assign to</label>
                  <div className="card" style={{ padding: 10 }}>
                    {viewMsg.forContactName || viewMsg.forContactId || "Unassigned"}
                  </div>
                </div>
                <div>
                  <label className="label">Attachment</label>
                  <div className="card" style={{ padding: 10 }}>
                    {viewMsg.fileUrl
                      ? kindLabel(viewMsg.attachmentKind || inferAttachmentKindFromUrl(viewMsg.fileUrl))
                      : "None"}
                  </div>
                </div>
              </div>

              <label className="label">Message</label>
              <div className="card" style={{ padding: 12, whiteSpace: "pre-wrap", marginBottom: 10 }}>
                {viewMsg.text || "-"}
              </div>

              {viewMsg.fileUrl &&
                (() => {
                  const kind = viewMsg.attachmentKind || inferAttachmentKindFromUrl(viewMsg.fileUrl);
                  if (kind === "picture") return <img src={viewMsg.fileUrl} alt="" className="pm-preview-thumb" />;
                  if (kind === "video")
                    return <video src={viewMsg.fileUrl} className="pm-preview-video" controls playsInline />;
                  if (kind === "audio")
                    return <audio src={viewMsg.fileUrl} className="pm-preview-audio" controls style={{ width: "100%" }} />;
                  return null;
                })()}

              <div className="form-actions" style={{ marginTop: 10 }}>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    try {
                      const whoRaw = viewMsg.forContactName || viewMsg.forContactId || "Personal Message";
                      const safeBase =
                        String(whoRaw)
                          .replace(/[^\w-]+/g, "_")
                          .replace(/^_+|_+$/g, "")
                          .slice(0, 64) || "Personal_Message";

                      const created = viewMsg?.created?.toDate ? viewMsg.created.toDate() : null;
                      const updated = viewMsg?.updated?.toDate ? viewMsg.updated.toDate() : null;

                      const details = [
                        "Personal Message",
                        `Assign to: ${viewMsg.forContactName || viewMsg.forContactId || "Unassigned"}`,
                        created ? `Created: ${created.toLocaleString()}` : "",
                        updated ? `Updated: ${updated.toLocaleString()}` : "",
                        "",
                        "Message:",
                        viewMsg.text || "",
                      ]
                        .filter(Boolean)
                        .join("\n");

                      downloadTextFile(`${safeBase}.txt`, details);

                      if (viewMsg.fileUrl || viewMsg.filePath) {
                        const base = viewMsg.filePath || toStoragePath(viewMsg.fileUrl);
                        const ok = await openDownloadForAttachment({
                          ownerUid: user.uid,
                          pathOrUrl: base,
                          suggestedName: viewMsg.fileName || `${safeBase}_attachment`,
                        });
                        if (!ok) alert("Could not generate a download link.");
                      }
                    } catch (e) {
                      alert("Download failed.");
                    }
                  }}
                  data-allow-readonly
                >
                  Download
                </button>

                <button className="btn btn-main" type="button" onClick={() => window.print()} data-allow-readonly>
                  Print
                </button>

                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const mailto = await buildForwardMailto({
                      ownerUid: user.uid,
                      pathOrUrl: viewMsg.filePath || viewMsg.fileUrl,
                      subject: `Personal Message${viewMsg.forContactName ? ` for ${viewMsg.forContactName}` : ""}`,
                      fields: {
                        "Assign to": viewMsg.forContactName || viewMsg.forContactId || "Unassigned",
                        Created: viewMsg?.created?.toDate ? viewMsg.created.toDate().toLocaleString() : "",
                        Updated: viewMsg?.updated?.toDate ? viewMsg.updated.toDate().toLocaleString() : "",
                        Attachment: viewMsg.fileUrl ? kindLabel(viewMsg.attachmentKind || inferAttachmentKindFromUrl(viewMsg.fileUrl)) : "None",
                        Message: viewMsg.text || "-",
                      },
                    });
                    window.location.href = mailto;
                  }}
                  data-allow-readonly
                >
                  Forward
                </button>

                <button className="btn btn-cancel" type="button" onClick={closeView} data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddContactModal open={showContactModal} onClose={() => setShowContactModal(false)} user={user} />

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/eugXQfrP1zI?si=KnnB1HqpdMScVK2D"
        title="How the Personal Messages page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Personal Messages page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>
                Write private notes for loved ones. Optionally attach a file, image, audio or video, and assign the message to a contact.
              </p>
              <ol>
                <li>Click <b>+</b> to add a message.</li>
                <li>Use <i>Assign to</i> to pick a contact or add a new one.</li>
                <li>Use the icons to upload or record. Save. Edit or delete later anytime.</li>
              </ol>
              <p className="note">
                Only you can see this page. Assignments help your contacts find relevant notes later.
              </p>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .dv-list{display:grid;grid-template-columns:1fr;gap:16px}
        .dv-item{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px}
        .dv-left{width:42px}
        .tile-thumb{width:42px;height:42px;border-radius:8px;object-fit:cover;background:#f4f5f9;display:block}
        .dv-actions{display:flex;gap:8px;justify-self:end}
        .pm-preview-thumb{width:140px;height:140px;object-fit:cover;border-radius:10px;margin-top:8px;display:block}
        .pm-preview-video{width:280px;max-width:100%;border-radius:10px}
        .pm-preview-audio{border-radius:10px}

        :root { --lwb-maroon:#2a0516; --lwb-grey:#645155; --lwb-accent:#e97c13; }
        .pm-kind-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}
        .pm-kind{border:1px solid rgba(0,0,0,0.08);background:#fff;border-radius:14px;padding:10px 8px;display:flex;flex-direction:column;align-items:center;gap:6px;font-weight:800;color:var(--lwb-maroon);cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,0.06);transition:transform .12s ease, box-shadow .12s ease, border-color .12s ease}
        .pm-kind:hover{transform:translateY(-1px);box-shadow:0 10px 22px rgba(0,0,0,0.10);border-color:rgba(0,0,0,0.14)}
        .pm-kind:active{transform:translateY(0)}
        .pm-kind-ico{font-size:1.25rem}
        .pm-kind span{font-size:.8rem;line-height:1}
        .pm-kind.active{border-color:rgba(233,124,19,0.7);box-shadow:0 10px 22px rgba(233,124,19,0.18)}
        .pm-kind.active .pm-kind-ico{color:var(--lwb-accent)}
        @media (max-width: 720px){
          .pm-kind-grid{grid-template-columns:repeat(3,minmax(0,1fr))}
        }

        .pm-attach-bar{margin-top:10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
        .pm-attach-pill{display:inline-flex;align-items:center;gap:6px;padding:8px 10px;border-radius:999px;background:#faf7f3;border:1px solid rgba(233,124,19,0.20);font-weight:700;color:#2a0516}
        .pm-attach-pill.warn{background:#fff7f7;border-color:rgba(180,0,0,0.22);color:#7a0000}
        .pm-attach-btn{padding:8px 12px}

        .pm-action-sheet{margin-top:10px;background:#fff;border:1px solid rgba(0,0,0,0.08);border-radius:14px;padding:12px;box-shadow:0 12px 28px rgba(0,0,0,0.08)}
        .pm-action-title{font-weight:900;color:var(--lwb-maroon);margin-bottom:8px}
        .pm-action-row{display:flex;gap:8px;flex-wrap:wrap}
        .pm-action-note{margin-top:8px;font-size:.9rem;color:#444}

        .pm-rec-wrap{margin-top:10px;background:#fff;border:1px solid rgba(0,0,0,0.08);border-radius:14px;padding:12px;box-shadow:0 12px 28px rgba(0,0,0,0.08)}
        .pm-rec-head{display:flex;align-items:center;justify-content:space-between;gap:10px}
        .pm-rec-title{font-weight:900;color:var(--lwb-maroon)}
        .pm-rec-video{width:100%;border-radius:12px;margin-top:10px;background:#000}
        .pm-rec-actions{margin-top:10px;display:flex;gap:8px;flex-wrap:wrap}
        .pm-rec-note{margin-top:8px;font-size:.9rem;color:#444}

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }
        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="messages" />
    </>
  );
}
